import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Instagram, ExternalLink, X } from 'lucide-react';
import { businessInfo } from '../data/mock';

gsap.registerPlugin(ScrollTrigger);

// Instagram posts with actual images
const instagramPosts = [
  { 
    id: 1, 
    image: "https://customer-assets.emergentagent.com/job_d0850d40-f350-4ecf-87ac-4e2d9723d534/artifacts/0pbswmya_insta%201.png",
    caption: "Commercial CCTV installation complete",
    alt: "CCTV camera installation Bayside Melbourne"
  },
  { 
    id: 2, 
    image: "https://customer-assets.emergentagent.com/job_d0850d40-f350-4ecf-87ac-4e2d9723d534/artifacts/h3tl2s2f_Insta%202.png",
    caption: "LED shelf lighting installation",
    alt: "LED lighting installation Carrum electrician"
  },
  { 
    id: 3, 
    image: "https://customer-assets.emergentagent.com/job_d0850d40-f350-4ecf-87ac-4e2d9723d534/artifacts/na1784vf_Insta%203.png",
    caption: "Living room chandelier and lighting setup",
    alt: "Residential lighting installation South East Melbourne"
  },
];

const InstagramWall = () => {
  const sectionRef = useRef(null);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.insta-title',
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      gsap.fromTo(
        '.insta-post',
        { scale: 0.8, opacity: 0 },
        {
          scale: 1,
          opacity: 1,
          duration: 0.5,
          stagger: 0.1,
          scrollTrigger: {
            trigger: '.insta-grid',
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white relative overflow-hidden">
      {/* Background Accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-securelec-red/5 rounded-full blur-[200px]" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="insta-title inline-flex items-center gap-2 text-securelec-red text-sm font-semibold tracking-widest uppercase mb-4">
            <Instagram className="w-4 h-4" />
            Follow Our Work
          </span>
          <h2 className="insta-title text-4xl md:text-5xl font-bold text-securelec-dark mb-6">
            <span className="text-securelec-red">@securelec_</span>
          </h2>
          <a
            href={businessInfo.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="insta-title inline-flex items-center gap-2 text-securelec-gray/60 hover:text-securelec-red transition-colors duration-300"
          >
            View on Instagram
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        {/* Instagram Grid - 3 images in one row */}
        <div className="insta-grid grid grid-cols-1 md:grid-cols-3 gap-4">
          {instagramPosts.map((post) => (
            <div
              key={post.id}
              className="insta-post group relative aspect-square cursor-pointer overflow-hidden rounded-xl"
              onClick={() => setSelectedPost(post)}
            >
              {/* Actual Image */}
              <img 
                src={post.image} 
                alt={post.alt}
                className="absolute inset-0 w-full h-full object-cover"
              />

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-securelec-red/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <Instagram className="w-8 h-8 text-white" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedPost && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPost(null)}
        >
          <button
            className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors"
            onClick={() => setSelectedPost(null)}
          >
            <X className="w-8 h-8" />
          </button>

          <div
            className="bg-white border border-gray-200 rounded-2xl overflow-hidden max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image Area */}
            <div className="aspect-square">
              <img 
                src={selectedPost.image} 
                alt={selectedPost.caption}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Caption */}
            <div className="p-6">
              <p className="text-securelec-gray/80">{selectedPost.caption}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default InstagramWall;
